/**
 * MainRouter
 *
 * This component handles the application's routing logic with support for:
 * 1. Feature-flag based routing (legacy vs new navigation experience)
 * 2. Route protection via ProtectedRoute
 * 3. Modal-style routes using React Router's location state
 */

import { MainRouter as LegacyMainRouter } from '@legacy/routers/MainRouter';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { isDev } from './legacy/utils/env';
import { NewDashboard } from './pages/reporting/NewDashboard';
import { ExpenseFaceliftRouter } from './routes/ExpenseFaceliftRouter';
import {
  ProjectRouter,
  ProjectLegacyRedirectsPure,
  ProjectLegacyFallback,
} from './routes/ProjectRouter';
import { DefaultLayout } from '@/components/layouts/DefaultLayout';
import { LegacyLayout } from '@/components/layouts/LegacyLayout';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { routes } from '@/constants/routes';
import {
  useHasDashboardsReporting,
  useHasEmployeePortfolio,
  useHasNewExpenseUI,
  useHasNewNavigation,
  useHasNewProjectManagement,
  useHasProjectPortfolio,
} from '@/hooks/featureFlags';
import { designSevenPagePath } from '@/legacy/designSevenPagePath';
import { PersonalPlan } from '@/legacy/screens/PersonalPlan';
import { ErrorTestingPage } from '@/pages/dev';
import { EmployeesPortfolio } from '@/pages/employees/portfolio';
import { Portfolio as ProjectPortfolio } from '@/pages/projects/portfolio';
import { Dashboard } from '@/pages/reporting/Dashboard';
import { DashboardsOverview } from '@/pages/reporting/DashboardsOverview';
import { LogoutRoute } from '@/routes/LogoutRoute';
import { ProtectedRoute } from '@/routes/ProtectedRoute';

export const MainRouter = () => {
  const hasNewNavigation = useHasNewNavigation();
  const hasProjectPortfolio = useHasProjectPortfolio();
  const hasEmployeePortfolio = useHasEmployeePortfolio();
  const hasNewExpenseUI = useHasNewExpenseUI();
  const hasDashboardsReporting = useHasDashboardsReporting();
  const hasNewProjectManagement = useHasNewProjectManagement();

  const location = useLocation();
  const { pathname, state } = location;

  // Extract background state for modal-style routing
  // This allows us to show a route as a modal while keeping the previous route visible underneath
  let background = state?.background;

  // Special case for personal plan page - set background to home if accessed directly
  if (pathname === designSevenPagePath.personalPlan && !background) {
    background = {
      ...location,
      pathname: designSevenPagePath.home,
      search: '', // Clear search params when falling back to home
    };
  }

  /**
   * Generate main application routes based on feature flag status
   *
   * This function returns different route configurations depending on whether
   * the new navigation experience is enabled. For the new experience, we use
   * the DefaultLayout for new routes and wrap legacy routes in both DefaultLayout
   * and LegacyLayout. For the old experience, we just use LegacyLayout.
   */
  const getRoutes = () => {
    if (hasNewNavigation) {
      return (
        <>
          {/* New navigation routes */}
          <Route element={<DefaultLayout />}>
            <Route
              key="logout"
              path={routes.logout()}
              element={<LogoutRoute />}
            />
            {hasProjectPortfolio && (
              <Route
                key="projects-portfolio"
                path={routes.projects.base()}
                element={<ProjectPortfolio />}
              />
            )}
            {hasEmployeePortfolio && (
              <Route
                key="employees-portfolio"
                path={routes.employees.base()}
                element={<EmployeesPortfolio />}
              />
            )}

            {hasNewProjectManagement && ProjectRouter()}
            {hasNewExpenseUI && ExpenseFaceliftRouter()}
            {/* We use a redirect to hide the available currencies page when new expense UI is enabled */}
            {hasNewExpenseUI && (
              <Route
                key="hide-available-currencies-page-redirect"
                path={legacyRoutes.settings.invoice.currencySetting}
                element={
                  <Navigate to={legacyRoutes.settings.general.index} replace />
                }
              />
            )}
            {hasDashboardsReporting && (
              <>
                <Route
                  key="dashboards"
                  path={routes.reporting.base()}
                  element={<DashboardsOverview />}
                />
                <Route
                  key="dashboard"
                  path={routes.reporting.dashboard(':id')}
                  element={<Dashboard />}
                />
                <Route
                  key="new-dashboard"
                  path={routes.reporting.new()}
                  element={<NewDashboard />}
                />
              </>
            )}
            {/* Development routes - only in development */}
            {isDev && (
              <Route
                key="error-testing"
                path={routes.dev.errorTesting()}
                element={<ErrorTestingPage />}
              />
            )}
          </Route>

          {/* Legacy URL redirects - must come BEFORE legacy catch-all routes */}
          {hasNewProjectManagement && ProjectLegacyRedirectsPure()}

          {/* Legacy routes wrapped in new navigation */}
          <Route element={<DefaultLayout isLegacyRoute />}>
            <Route element={<LegacyLayout isInNewNavigation />}>
              {/* Legacy ProjectManagement fallback route (for non-project-specific pages) */}
              {hasNewProjectManagement && ProjectLegacyFallback()}
              {LegacyMainRouter(pathname)}
            </Route>
          </Route>
        </>
      );
    } else {
      // Use only legacy routes when user does not have new navigation
      return (
        <Route element={<LegacyLayout isInNewNavigation={false} />}>
          {LegacyMainRouter(pathname)}
        </Route>
      );
    }
  };

  /**
   * Generate background routes for modal-style UI
   *
   * These routes are rendered when a background state is present,
   * allowing us to show modals while keeping the previous page in view.
   * Once we have modal in new layout we need to wrap this in DefaultLayout as well.
   */
  const getBackgroundRoutes = () => {
    return (
      <Route element={<LegacyLayout />}>
        <Route
          path={designSevenPagePath.personalPlan}
          element={<PersonalPlan />}
        />
      </Route>
    );
  };

  // Return the router structure with both main and background routes
  return (
    <>
      {/* Main routes - use background location if available */}
      <Routes location={background || location}>
        <Route element={<ProtectedRoute />}>{getRoutes()}</Route>
      </Routes>

      {/* 
        Background routes for modal UI - only rendered when background state exists
        Uses the current location to render "on top" of the main routes
      */}
      {background && <Routes>{getBackgroundRoutes()}</Routes>}
    </>
  );
};
